'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { type ChainId, ChainUtils } from '@morpho-org/blue-sdk';
import { isDefined } from '@morpho-org/morpho-ts';
import { ClipboardButton, DataList } from '@morpho-org/ui-v6/client';
import { ArrowDiagonalUp } from '@repo/ui/icons';
import { AbbreviatedHex } from '@repo/web3';
import { skipToken, useQuery } from '@tanstack/react-query';
import type { TrustedByCurator } from '@/components/MarketPage/utils/createMarketTrustedByCurators';
import { ButtonLink } from '@/components/common/ButtonLink';
import { TextAddress } from '@/components/common/TextAddress';
import { WarningIcon } from '@/components/common/WarningIcon';
import { MarketTrustedByCuratorStack } from '@/components/MarketPage/components/MarketTrustedByCuratorStack';
import { formatTimestampToDate } from '@/helpers/date';
import { orpc } from '@/lib/orpc/client';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import type { MarketOracles } from '../oracles.contract';
import { AdminValue, ProviderValue } from '../OracleFeeds/OracleFeedParts';
import { DECODING_REFRESH_MS, hasMultipleOracles, toOracleFeeds } from '../OracleFeeds/oracleFeeds';
import { type OracleFeedsState, OraclePriceConstruction } from '../OracleFeeds/OraclePriceConstruction';
import { HeaderOracles } from './HeaderOracles';
import { toOracleSummary } from './oracleSummary';
import { ValueSecured } from './ValueSecured';
export interface OracleAttributesClientProps {
  chainId: ChainId;
  /** `null` on an idle market, which has no oracle. */
  oracle: Address | null;
  oracles: MarketOracles;
  trustedByCurators: TrustedByCurator[];
  /** The server's decoding read failed, so no decoding was hydrated. */
  decodingFailed: boolean;
}

/** Oracle attributes and the decoded feeds behind the price. */
export default function OracleAttributesClient(t0) {
  const $ = _c(76);
  const {
    chainId,
    oracle,
    oracles,
    trustedByCurators,
    decodingFailed
  } = t0;
  const flags = useFeatureFlags();
  let t1;
  if ($[0] !== chainId || $[1] !== flags.oracleFeeds || $[2] !== oracle) {
    t1 = orpc.oracle.decoding.queryOptions({
      input: flags.oracleFeeds && oracle !== null ? {
        chainId,
        address: oracle
      } : skipToken,
      staleTime: DECODING_REFRESH_MS,
      refetchInterval: DECODING_REFRESH_MS
    });
    $[0] = chainId;
    $[1] = flags.oracleFeeds;
    $[2] = oracle;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data
  } = useQuery(t1);
  let t2;
  if ($[4] !== data || $[5] !== decodingFailed) {
    t2 = data ? data.decoding && !hasMultipleOracles(data.decoding) ? toOracleFeeds(data.decoding) : null : decodingFailed ? "error" : null;
    $[4] = data;
    $[5] = decodingFailed;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const feeds = t2;
  let t3;
  if ($[7] !== oracles) {
    t3 = toOracleSummary(oracles);
    $[7] = oracles;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    marketPrice,
    oraclePrice,
    valueSecured,
    oracleWarnings
  } = t3;
  const decoded = feeds !== null && feeds !== "error" ? feeds : null;
  const creationTxHash = oracles.oracle?.creationTxHash;
  let t4;
  if ($[9] !== oracles.oracle) {
    t4 = isDefined(oracles.oracle?.creationTimestamp) ? formatTimestampToDate(BigInt(oracles.oracle.creationTimestamp)) : null;
    $[9] = oracles.oracle;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const createdOn = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <HeaderOracles />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <DataList.LeftItem label="Oracle contract" />;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== chainId || $[14] !== oracle) {
    t7 = <DataList.Line>{t6}<DataList.RightItem>{oracle === null ? <span className="text-foreground text-xs">—</span> : <TextAddress textClassName="text-xs" address={oracle} targetChainId={chainId} toastMessage="Oracle address copied to clipboard." tooltipMessage="Copy oracle address to clipboard." textColorClass="text-foreground" />}</DataList.RightItem></DataList.Line>;
    $[13] = chainId;
    $[14] = oracle;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DataList.LeftItem label="Oracle creation" tooltip="Oracle deployment transaction." />;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== chainId || $[18] !== creationTxHash) {
    t9 = <DataList.Line>{t8}<DataList.RightItem>{creationTxHash ? <div className="gap-2xs flex items-center"><AbbreviatedHex value={creationTxHash} className="text-foreground text-xs" /><ClipboardButton size="sm" toCopy={creationTxHash} toastMessage="Transaction hash copied to clipboard." tooltipMessage="Copy transaction hash to clipboard." /><ButtonLink href={ChainUtils.getExplorerTransactionUrl(chainId, creationTxHash)} className="flex" buttonProps={{
            variant: "ghost",
            size: "sm",
            iconRight: undefined,
            iconAlone: <ArrowDiagonalUp />,
            iconAloneProps: {
              className: "text-secondary-foreground"
            }
          }} /></div> : <span className="text-foreground text-xs">—</span>}</DataList.RightItem></DataList.Line>;
    $[17] = chainId;
    $[18] = creationTxHash;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DataList.LeftItem label="Created on" />;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = createdOn ?? "\u2014";
  let t12;
  if ($[21] !== t11) {
    t12 = <DataList.Line>{t10}<DataList.RightItem label={t11} /></DataList.Line>;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <DataList.LeftItem label="Providers" />;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const t14 = decoded?.providers;
  let t15;
  if ($[24] !== t14) {
    t15 = <DataList.Line>{t13}<DataList.RightItem><ProviderValue providers={t14} /></DataList.RightItem></DataList.Line>;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <DataList.LeftItem label="Admin" tooltip="The decoder identifies these addresses as able to change a feed or setting in this oracle." />;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  const t17 = decoded?.admins ?? null;
  let t18;
  if ($[27] !== chainId || $[28] !== t17) {
    t18 = <DataList.Line>{t16}<DataList.RightItem><span className="text-foreground text-xs"><AdminValue chainId={chainId} admins={t17} withCopy={true} align="right" /></span></DataList.RightItem></DataList.Line>;
    $[27] = chainId;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== t12 || $[31] !== t15 || $[32] !== t18 || $[33] !== t7 || $[34] !== t9) {
    t19 = <DataList.Column>{t7}{t9}{t12}{t15}{t18}</DataList.Column>;
    $[30] = t12;
    $[31] = t15;
    $[32] = t18;
    $[33] = t7;
    $[34] = t9;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <DataList.LeftItem label="Trusted by" />;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== trustedByCurators) {
    t21 = <DataList.Line>{t20}<DataList.RightItem><MarketTrustedByCuratorStack trustedByCurators={trustedByCurators} maxTokensCount={10} avatarSize="small" align="right" /></DataList.RightItem></DataList.Line>;
    $[37] = trustedByCurators;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <span className="text-secondary-foreground text-xs">Oracle price</span>;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== oracleWarnings) {
    t23 = oracleWarnings.length > 0 && <WarningIcon warnings={oracleWarnings} isSimple={true} />;
    $[40] = oracleWarnings;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t23) {
    t24 = <DataList.LeftItem>{t22}{t23}</DataList.LeftItem>;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== oraclePrice) {
    t25 = <DataList.RightItem label={oraclePrice} />;
    $[44] = oraclePrice;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== t24 || $[47] !== t25) {
    t26 = <DataList.Line>{t24}{t25}</DataList.Line>;
    $[46] = t24;
    $[47] = t25;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <DataList.LeftItem label="DefiLlama price" tooltip="This price is sourced from DefiLlama for informational purposes only. It does not affect your position. The oracle price determines liquidations and health factor for this market." />;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== marketPrice) {
    t28 = <DataList.Line>{t27}<DataList.RightItem label={marketPrice} /></DataList.Line>;
    $[50] = marketPrice;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <DataList.LeftItem label="Value secured" tooltip="Total value of the collateral and liquidity secured by the oracle." />;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] !== chainId || $[54] !== oracles.oracle || $[55] !== valueSecured) {
    t30 = <DataList.Line>{t29}<DataList.RightItem><ValueSecured chainId={chainId} valueSecured={valueSecured} oracle={oracles.oracle} /></DataList.RightItem></DataList.Line>;
    $[53] = chainId;
    $[54] = oracles.oracle;
    $[55] = valueSecured;
    $[56] = t30;
  } else {
    t30 = $[56];
  }
  let t31;
  if ($[57] !== t21 || $[58] !== t26 || $[59] !== t28 || $[60] !== t30) {
    t31 = <DataList.Column>{t21}{t26}{t28}{t30}</DataList.Column>;
    $[57] = t21;
    $[58] = t26;
    $[59] = t28;
    $[60] = t30;
    $[61] = t31;
  } else {
    t31 = $[61];
  }
  let t32;
  if ($[62] !== t19 || $[63] !== t31) {
    t32 = <div className="gap-2xs flex w-full flex-col">{t5}<DataList.Root>{t19}{t31}</DataList.Root></div>;
    $[62] = t19;
    $[63] = t31;
    $[64] = t32;
  } else {
    t32 = $[64];
  }
  let t33;
  if ($[65] !== chainId || $[66] !== data?.outcome || $[67] !== feeds || $[68] !== oracle || $[69] !== oracles.collateralToken?.logoUri || $[70] !== oracles.collateralToken?.symbol || $[71] !== oracles.loanToken) {
    t33 = oracle !== null && <OraclePriceConstruction chainId={chainId} oracle={oracle} feeds={feeds} outcome={data?.outcome} symbols={{
      collateral: oracles.collateralToken?.symbol ?? null,
      loan: oracles.loanToken.symbol
    }} logos={{
      collateral: oracles.collateralToken?.logoUri ?? null,
      loan: oracles.loanToken.logoUri
    }} />;
    $[65] = chainId;
    $[66] = data?.outcome;
    $[67] = feeds;
    $[68] = oracle;
    $[69] = oracles.collateralToken?.logoUri;
    $[70] = oracles.collateralToken?.symbol;
    $[71] = oracles.loanToken;
    $[72] = t33;
  } else {
    t33 = $[72];
  }
  let t34;
  if ($[73] !== t32 || $[74] !== t33) {
    t34 = <div className="gap-lg sm:gap-xl flex w-full flex-col">{t32}{t33}</div>;
    $[73] = t32;
    $[74] = t33;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  return t34;
}